import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client.js'
import { endpoints } from '../../api/endpoints.js'
import {
  getOrganization,
  regenerateOrgCode,
  listOrgStaffLinks,
  updateOrgStaffLink,
  removeOrgStaffLink,
  bulkUploadOrgRoster,
  listOrgAdminLinks,
  listCoAdminInvitations,
  inviteCoAdmin,
  revokeCoAdminInvitation,
  resendCoAdminInvitation,
  setOrganizationLogo,
  setOrgAnnualSubscription,
  listOrgCentres,
  createOrgCentre,
  updateOrgCentre,
  regenerateOrgCentreCode,
  deactivateOrgCentre,
  listOrgStatements,
  downloadOrgStatement,
} from '../../api/organizations.js'
import { formatDateTime, unwrapList } from '../../utils/format.js'
import { useDebounce } from '../../hooks/useDebounce.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import CatalogueImageThumb from '../../components/CatalogueImageThumb.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import QuarterlyReportButton from '../../components/QuarterlyReportButton.jsx'
import { useAuth } from '../../auth/AuthProvider.jsx'

/**
 * Subscribing-organization dashboard. Shows aggregate workforce-health
 * metrics for an org's enrolled staff. Org admins see only their own
 * org (auto-scoped via OrganizationAdminLink). Platform admins can pick
 * any organization from a dropdown.
 *
 * Privacy: every breakdown bucket is k=5 anonymity-floor suppressed
 * server-side — the FE just renders what the server returns.
 */
export default function OrgDashboard() {
  const { user } = useAuth()
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser)
  const linkedOrgId = user?.organization_admin_profile?.id ?? null
  const linkedOrgName = user?.organization_admin_profile?.name ?? ''

  const orgsQ = useQuery({
    queryKey: ['orgs-list'],
    queryFn: async () => {
      const { data } = await api.get(endpoints.organizations)
      return unwrapList(data)
    },
    enabled: isAdmin,
  })
  const [orgId, setOrgId] = useState(linkedOrgId)
  const [days, setDays] = useState(30)
  if (isAdmin && !orgId && orgsQ.data?.[0]?.id) setOrgId(orgsQ.data[0].id)

  // Tabs replace the long-scroll layout. Members is the default —
  // it's where org admins land for daily roster + onboarding work.
  // Co-administrators and Workforce health are second-tier.
  const TABS = [
    { id: 'members', label: 'Members' },
    { id: 'centres', label: 'Centres' },
    { id: 'admins',  label: 'Co-administrators' },
    { id: 'statements', label: 'Statements' },
    { id: 'health',  label: 'Workforce health' },
  ]
  const [tab, setTab] = useState('members')

  const dashQ = useQuery({
    queryKey: ['org-dashboard', orgId, days],
    queryFn: async () => {
      const { data } = await api.get(`${endpoints.organizations}${orgId}/dashboard/`, {
        params: { days },
      })
      return data
    },
    enabled: Boolean(orgId),
  })

  if (!isAdmin && !linkedOrgId) {
    return (
      <Card>
        <CardHeader
          title="Workforce health dashboard"
          subtitle="Aggregate analytics for your organization's enrolled staff"
        />
        <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
          <b>Account not linked to an organization yet.</b>
          <p className="mt-1">
            Ask the platform admin to bind your user
            (<span className="font-mono">{user?.username}</span>) to an
            organization via Django admin → Organizations → Organization
            admin links → Add.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Workforce health dashboard"
          subtitle="Aggregate analytics — individual records are never exposed (k=5 privacy floor)."
          right={
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Window:</label>
              <select
                value={days} onChange={(e) => setDays(Number(e.target.value))}
                className="border rounded px-2 py-1 text-xs bg-white"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={180}>Last 180 days</option>
                <option value={365}>Last 365 days</option>
              </select>
            </div>
          }
        />
        {isAdmin ? (
          <div className="mt-2">
            <label className="text-xs uppercase tracking-wide text-slate-500">
              Organization
              <span className="ml-2 text-amber-700 normal-case font-normal">(admin — pick any)</span>
            </label>
            <select
              value={orgId || ''} onChange={(e) => setOrgId(Number(e.target.value))}
              className="mt-1 w-full border rounded px-3 py-2 text-sm bg-white"
            >
              {(orgsQ.data || []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} {o.org_type ? `· ${o.org_type}` : ''}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="mt-2 text-sm">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-2">Organization</span>
            <span className="font-semibold text-slate-800">{linkedOrgName}</span>
            {user?.organization_admin_profile?.is_primary && (
              <span className="ml-2 text-[10px] uppercase border rounded-full px-2 py-0.5 bg-wantok-50 text-wantok-700 border-wantok-100">
                primary admin
              </span>
            )}
          </div>
        )}
      </Card>

      {dashQ.error && <ErrorBanner error={dashQ.error} onRetry={() => dashQ.refetch()} />}
      {dashQ.isLoading && <Card><div className="text-sm text-slate-400">Loading dashboard…</div></Card>}

      {/* Brand header — org logo + name displayed prominently at the
          top of every org admin's dashboard, with a compact 3-tile
          impact strip on the right (members / encounters / savings)
          so the value ICER delivers reads at-a-glance. Platform admin
          can click the logo (or placeholder) to upload/replace; org
          admins see read-only. */}
      {orgId && (
        <OrgBrandHeader
          orgId={orgId}
          dashboardData={dashQ.data}
          windowDays={days}
        />
      )}

      {orgId && <QuarterlyReportButton scope="org" orgId={orgId} />}

      {/* Tab strip — Members (roster + onboarding) is the default
          because that's the daily-action surface. Co-administrators
          handles admin invites. Workforce health is the analytics
          dashboard (read-only metrics + value & impact breakdown).
          Same visual pattern as the doctor dashboard's tab strip. */}
      {orgId && (
        <div className="border-b-2 border-slate-200 flex flex-wrap gap-2">
          {TABS.map((t) => {
            const active = tab === t.id
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={
                  `px-6 py-3 text-base tracking-wide border-b-[3px] -mb-[2px] transition-colors ` +
                  (active
                    ? 'border-wantok-700 text-wantok-800 font-bold bg-wantok-50/60'
                    : 'border-transparent text-slate-600 font-semibold hover:text-wantok-800 hover:bg-slate-50')
                }
              >
                {t.label}
              </button>
            )
          })}
        </div>
      )}

      {/* ---- Tab panels ---- */}

      {/* Members tab — self-enrollment code + bulk roster import +
          enrolled members list. Roster operations cluster here. */}
      {tab === 'members' && orgId && (
        <div className="space-y-4">
          <SelfEnrollCodeCard orgId={orgId} />
          <BulkImportCard orgId={orgId} />
          <EnrolledMembersCard orgId={orgId} />
        </div>
      )}

      {/* Centres tab — multi-site customers (OPIC plantations, NYDA
          regional offices, NSO field bureaux, NFA stations, PNG
          Customs ports, etc.) manage their per-centre rows here. Each
          centre has its own self-enrolment code + member count. */}
      {tab === 'centres' && orgId && (
        <CentresCard orgId={orgId} />
      )}

      {/* Co-administrators tab — list of admins on this org plus
          pending invitations + invite-co-admin form. */}
      {tab === 'admins' && orgId && (
        <CoAdminsCard orgId={orgId} />
      )}

      {/* Statements tab — read-only list of issued Value & Billing
          statements with PDF download. ICER issues them (on the
          /superuser/statements surface); org admins read/download their
          own here. Backend scopes the list to the org. */}
      {tab === 'statements' && orgId && (
        <StatementsCard orgId={orgId} />
      )}

      {/* Workforce health tab — analytics, demographics, diagnoses,
          lab/imaging/Rx/ambulance counts, and the value & impact
          breakdown. All k=5 anonymity-floor suppressed server-side. */}
      {tab === 'health' && dashQ.data && (
        <DashboardBody data={dashQ.data} isAdmin={isAdmin} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Self-enrollment code card
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Brand header — logo + org name as the visual "this is who you are"
// surface at the top of the dashboard.
//
// Sized so the logo reads at a glance on a 13" laptop (96 px) without
// overwhelming the content. Falls back to a tasteful placeholder
// (initials of the org name on a wantok-tinted background) when no
// logo has been uploaded yet — better than a blank box, communicates
// "this surface is ready, just hasn't been branded".
//
// Platform admin gets the click-to-edit upload modal via the same
// `CatalogueImageThumb` component used by the catalogue pages. Org
// admins see the logo read-only — they can request a logo update by
// emailing the platform team.
// ---------------------------------------------------------------------------
function StatementsCard({ orgId }) {
  const q = useQuery({
    queryKey: ['org-statements', orgId],
    queryFn: () => listOrgStatements(orgId),
    enabled: Boolean(orgId),
  })
  const fmtK = (x) => `K${Number(x || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  async function download(id, label) {
    const blob = await downloadOrgStatement(id)
    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `wantok_statement_${label || id}.pdf`
    document.body.appendChild(a); a.click(); a.remove()
    window.URL.revokeObjectURL(url)
  }

  const rows = q.data || []
  return (
    <Card>
      <CardHeader
        title="Value & Billing statements"
        subtitle="Issued statements for your organisation. Download the PDF for your records."
      />
      {q.error && <ErrorBanner error={q.error} />}
      {q.isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-400 italic py-4 text-center">
          No statements have been issued yet.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
            <tr>
              <th className="text-left p-2">Period</th>
              <th className="text-right p-2">Amount billable</th>
              <th className="text-right p-2">Value delivered</th>
              <th className="text-left p-2">Issued</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50">
                <td className="p-2 font-medium text-slate-800">{s.label || `${s.period_start} – ${s.period_end}`}</td>
                <td className="p-2 text-right text-sky-700 font-semibold">{fmtK(s.amount_billable_pgk)}</td>
                <td className="p-2 text-right">{fmtK(s.total_value_delivered_pgk)}</td>
                <td className="p-2 text-slate-500">{s.issued_at ? formatDateTime(s.issued_at) : '—'}</td>
                <td className="p-2 text-right">
                  <button
                    disabled={!s.has_pdf}
                    onClick={() => download(s.id, s.label)}
                    className="text-xs px-2.5 py-1 rounded-full border border-wantok-300 text-wantok-700 hover:bg-wantok-50 disabled:opacity-40"
                  >
                    {s.has_pdf ? '⬇ PDF' : 'No PDF'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}

function OrgBrandHeader({ orgId, dashboardData, windowDays }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const isPlatformAdmin = Boolean(user?.is_staff || user?.is_superuser)

  const orgQ = useQuery({
    queryKey: ['org-detail', orgId],
    queryFn: () => getOrganization(orgId),
    enabled: Boolean(orgId),
  })
  const org = orgQ.data

  if (orgQ.isLoading) {
    return (
      <Card>
        <div className="text-sm text-slate-400">Loading organisation…</div>
      </Card>
    )
  }
  if (!org) return null

  return (
    <Card>
      <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
        {/* Brand block — logo + org name + metadata. Takes the left
            half on desktop, full width on mobile. */}
        <div className="flex items-center gap-5 min-w-0 flex-1">
          <div className="shrink-0">
            {org.logo_url ? (
              <CatalogueImageThumb
                url={org.logo_url}
                alt={`${org.name} logo`}
                editable={isPlatformAdmin}
                size={96}
                onUpload={async (file) => {
                  await setOrganizationLogo(orgId, file)
                  qc.invalidateQueries({ queryKey: ['org-detail', orgId] })
                }}
                onClear={async () => {
                  await setOrganizationLogo(orgId, null)
                  qc.invalidateQueries({ queryKey: ['org-detail', orgId] })
                }}
              />
            ) : (
              <OrgLogoPlaceholder
                org={org}
                isPlatformAdmin={isPlatformAdmin}
                orgId={orgId}
                onUploaded={() => qc.invalidateQueries({ queryKey: ['org-detail', orgId] })}
              />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-[0.18em] text-wantok-700 font-semibold">
              Organisation
            </div>
            <h1 className="text-2xl font-display font-semibold text-slate-900 mt-0.5 truncate">
              {org.name}
            </h1>
            <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-2">
              {org.org_type && (
                <span className="border border-slate-200 rounded-full px-2 py-0.5 capitalize">
                  {org.org_type.replace('_', ' ')}
                </span>
              )}
              {org.industry && <span>· {org.industry}</span>}
              {org.subscription_tier && (
                <span
                  className="border border-wantok-200 bg-wantok-50 text-wantok-700 rounded-full px-2 py-0.5 uppercase text-[10px] tracking-wide cursor-help"
                  title={`${org.subscription_tier} subscription tier — set by your Wantok Telehealth account contact.`}
                >
                  {org.subscription_tier}
                </span>
              )}
              {!org.is_active && (
                <span className="border border-rose-300 text-rose-700 bg-rose-50 rounded-full px-2 py-0.5 uppercase text-[10px]">
                  inactive
                </span>
              )}
            </div>
            {isPlatformAdmin && !org.logo_url && (
              <div className="text-[11px] text-slate-500 italic mt-2">
                No logo on file. Click the placeholder to upload — PNG/JPG/WEBP, ≤5 MB.
              </div>
            )}
          </div>
        </div>

        {/* Impact strip — three compact tiles surfacing the headline
            "what ICER Health is doing for this org" numbers at the
            top of the page. Pulled from the dashboard payload the
            parent already loads, so no extra API call. The deeper
            ValueImpactSection lower down has the full breakdown. */}
        <OrgImpactStrip data={dashboardData} windowDays={windowDays} org={org} />
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Compact 3-tile impact strip rendered on the right side of the brand
// header. Stays out of the way visually — small numbers in the wantok
// palette, not the dominant feature of the page — but readable at a
// glance so the org admin sees the value summary the moment they
// land on the dashboard.
// ---------------------------------------------------------------------------
function OrgImpactStrip({ data, windowDays, org }) {
  // Loading-state skeleton — empty tiles so the layout doesn't shift
  // when dashboard data arrives.
  if (!data) {
    return (
      <div className="flex items-stretch gap-3 lg:gap-5 shrink-0 border-t lg:border-t-0 lg:border-l border-slate-100 pt-4 lg:pt-0 lg:pl-8">
        <ImpactTile label="Members" value="—" />
        <ImpactTile label="Encounters" value="—" />
        <ImpactTile label="Savings" value="—" />
      </div>
    )
  }

  // Prefer the org's enrolled_count (server-computed, cheap) over the
  // dashboard's headline.staff_active — both are the same number but
  // enrolled_count survives the privacy floor.
  const members = org?.enrolled_count ?? data?.headline?.staff_active ?? 0
  const encounters = data?.encounters?.total ?? 0
  // The header "value delivered" KPI uses the Phase 6.5 activity-driven
  // valuation — the SAME number as the "Subscription & Value Delivered"
  // section below — so the headline reflects ALL real value streams
  // (online + in-person consults, bedside/screening services, chronic-case
  // management, plus manual site-visit/logistics entries), priced at the
  // org's rate card. This is gross value delivered; it rises with every
  // service rendered. The net-vs-subscription / ROI story lives in the
  // section below. Falls back to the legacy proxy figure for old payloads.
  const savings = Number(
    (data?.activity_value_delivered?.total_value_pgk ?? 0)
    + (data?.icer_value_delivered?.total_value_pgk ?? 0),
  ) || Number(data?.value_impact?.net_value ?? 0)

  return (
    <div className="flex items-stretch gap-3 lg:gap-5 shrink-0 border-t lg:border-t-0 lg:border-l border-slate-100 pt-4 lg:pt-0 lg:pl-8">
      <ImpactTile
        label="Members"
        value={members.toLocaleString()}
        sublabel="enrolled"
      />
      <ImpactTile
        label={`Encounters · ${windowDays}d`}
        value={encounters.toLocaleString()}
        sublabel="this period"
      />
      <ImpactTile
        label="Savings"
        value={`K ${formatCompactPGK(savings)}`}
        sublabel="value delivered"
        accent
      />
    </div>
  )
}

// Single tile — small typography, big-number-front-and-centre. The
// `accent` flag styles the savings tile in the wantok palette so the
// "money saved" reads as the most important number without anything
// being ALL-CAPS-shouting.
function ImpactTile({ label, value, sublabel, accent }) {
  return (
    <div className="text-center min-w-[7rem]">
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold whitespace-nowrap">
        {label}
      </div>
      <div
        className={
          'text-2xl font-display font-bold mt-0.5 ' +
          (accent ? 'text-wantok-700' : 'text-slate-900')
        }
      >
        {value}
      </div>
      {sublabel && (
        <div className="text-[10px] text-slate-400 mt-0.5">{sublabel}</div>
      )}
    </div>
  )
}

// Compact PGK formatting: "12,450" → "12.5k", "1,250,000" → "1.25M".
// Keeps the big-number tile readable on small screens without
// truncating mid-digit. Threshold is 10k — below that, full digits
// (e.g. "PGK 8,250") fits fine.
function formatCompactPGK(n) {
  const num = Number(n || 0)
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(2).replace(/\.?0+$/, '') + 'M'
  if (num >= 10_000)    return (num / 1_000).toFixed(1).replace(/\.?0+$/, '') + 'k'
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

// Placeholder shown when no logo has been uploaded yet. Renders the
// first 1-2 letters of the org name on a wantok-tinted gradient — a
// tasteful "initials avatar" pattern that reads as a real brand mark
// rather than a "missing image" icon. Platform admin can click to
// open the upload modal.
function OrgLogoPlaceholder({ org, isPlatformAdmin, orgId, onUploaded }) {
  const initials = (org.name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('') || '?'

  // Reuse the CatalogueImageThumb's editable=true path with `url=null`
  // so the upload modal opens on click. The component renders a small
  // "no img" placeholder by default; for the brand header we want
  // something more polished, so we render our own inline at 96px.
  if (!isPlatformAdmin) {
    return (
      <div
        className="rounded-xl flex items-center justify-center font-display font-bold text-3xl text-wantok-700 bg-gradient-to-br from-wantok-50 to-wantok-100 border border-wantok-200"
        style={{ width: 96, height: 96 }}
        aria-label={`${org.name} placeholder logo`}
      >
        {initials}
      </div>
    )
  }
  // Admin: clickable, opens the upload modal via CatalogueImageThumb.
  return (
    <CatalogueImageThumb
      url={null}
      alt={org.name}
      editable={true}
      size={96}
      onUpload={async (file) => {
        await setOrganizationLogo(orgId, file)
        onUploaded?.()
      }}
    />
  )
}

function SelfEnrollCodeCard({ orgId }) {
  const qc = useQueryClient()
  const orgQ = useQuery({
    queryKey: ['org-detail', orgId],
    queryFn: () => getOrganization(orgId),
    enabled: Boolean(orgId),
  })
  const org = orgQ.data

  // Pull centres so we can surface a cross-link once the org has at
  // least one site set up — clarifies that the org-wide code is the
  // catch-all, not the only option for staff signup.
  const centresQ = useQuery({
    queryKey: ['org-centres', orgId],
    queryFn: () => listOrgCentres(orgId),
    enabled: Boolean(orgId),
  })
  const activeCentres = (centresQ.data || []).filter((c) => c.is_active)

  const regen = useMutation({
    mutationFn: () => regenerateOrgCode(orgId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-detail', orgId] }),
  })

  const [copied, setCopied] = useState('')   // 'code' | 'url' | ''
  async function copy(value, kind) {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(kind)
      setTimeout(() => setCopied(''), 2000)
    } catch {
      // Clipboard permission can be denied on insecure origins (HTTP).
      // The value is visible inline so the admin can copy manually.
    }
  }

  function onRegenerate() {
    const ok = window.confirm(
      `Regenerate the self-enrollment code for ${org?.name || 'this organization'}?\n\n` +
      `The current code will stop working immediately. Anyone with the old ` +
      `code on a printed flyer or email will need the new one.`
    )
    if (ok) regen.mutate()
  }

  if (!orgQ.isLoading && !org) {
    return null  // permission-denied or 404 — silently skip the card
  }

  return (
    <Card>
      <CardHeader
        title="Organisation-wide enrolment code"
        subtitle="Catch-all code for staff with no specific site. They self-register at /register and are auto-linked to your organisation; their record is not tagged to any centre."
      />
      {activeCentres.length > 0 && (
        <div className="mb-3 text-xs text-wantok-800 bg-wantok-50/70 border border-wantok-100 rounded px-3 py-2">
          <b>You also have {activeCentres.length} centre-specific code{activeCentres.length === 1 ? '' : 's'}</b>
          {' '}on the <b>Centres</b> tab. Use those for staff at a known
          site so workforce-health analytics can compare across locations.
        </div>
      )}
      {orgQ.isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (
        <div className="space-y-3">
          {/* Big code + copy */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="font-mono font-bold text-2xl tracking-wider bg-wantok-50 border-2 border-wantok-200 text-wantok-900 rounded px-4 py-2 select-all">
              {org?.self_enroll_code || '—'}
            </div>
            <button
              type="button"
              onClick={() => copy(org.self_enroll_code, 'code')}
              disabled={!org?.self_enroll_code}
              className="text-xs border border-slate-300 hover:border-wantok-400 rounded px-3 py-1.5"
            >
              {copied === 'code' ? '✓ Copied' : 'Copy code'}
            </button>
            <button
              type="button"
              onClick={onRegenerate}
              disabled={regen.isPending}
              className="text-xs border border-rose-300 text-rose-700 hover:bg-rose-50 rounded px-3 py-1.5 disabled:opacity-60"
              title="Mint a fresh code. The current one will stop working immediately."
            >
              {regen.isPending ? 'Regenerating…' : 'Regenerate'}
            </button>
            {org?.enrolled_count != null && (
              <span
                className="text-xs text-slate-500 ml-auto"
                title={
                  'Total active members linked to your organisation, regardless of how '
                  + 'they joined (self-registration with the current or previous code, '
                  + 'manual admin add, or bulk import). Code-specific attribution is a '
                  + 'planned follow-up.'
                }
              >
                <b className="text-slate-800">{org.enrolled_count}</b>
                {' '}active member{org.enrolled_count === 1 ? '' : 's'} in this organisation
              </span>
            )}
          </div>

          {/* Shareable URL */}
          {org?.enrolment_url && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="uppercase tracking-wide text-slate-500 mr-1">Shareable link</span>
              <span className="font-mono bg-slate-50 border border-slate-200 rounded px-2 py-1 truncate min-w-0 max-w-full">
                {org.enrolment_url}
              </span>
              <button
                type="button"
                onClick={() => copy(org.enrolment_url, 'url')}
                className="border border-slate-300 hover:border-wantok-400 rounded px-2 py-1"
              >
                {copied === 'url' ? '✓ Copied' : 'Copy link'}
              </button>
            </div>
          )}

          {regen.isError && (
            <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 text-sm">
              Could not regenerate the code. Try again, or ask the platform admin.
            </div>
          )}

          <p className="text-[11px] text-slate-500">
            Tip: print the code on a poster, paste it into onboarding emails, or
            share the link in your staff Slack / Teams channel. Patients enter
            it on the sign-up form to be enrolled under <b>{org?.name}</b>.
          </p>
        </div>
      )}
    </Card>
  )
}

function DashboardBody({ data, isAdmin = false }) {
  // Sub-tab state — DashboardBody breaks the workforce-health surface
  // into four logical groupings so org admins don't face a 10-block
  // vertical scroll. Default to "Value & ROI" because that's the answer
  // to the first question org admins always ask: "what are we getting?".
  //
  // A 5th tab, "ICER engagement", is appended for platform admins ONLY.
  // It surfaces ICER's cost-side cashflow narrative (per_org_economics
  // from the API) which org admins must never see — gated server-side
  // (the field isn't included in the response unless user.is_staff is
  // true) AND here in the FE so the tab doesn't render at all.
  const SUB_TABS = [
    {
      id: 'value',
      label: 'Value & ROI',
      hint: 'Savings delivered, ROI, methodology',
    },
    {
      id: 'workforce',
      label: 'Workforce',
      hint: 'Staff counts, demographics, chronic disease',
    },
    {
      id: 'clinical',
      label: 'Clinical activity',
      hint: 'Encounters, prescriptions, labs, imaging, ambulance',
    },
    {
      id: 'spend',
      label: 'Spend & catalogue',
      hint: 'Subscription, contracted prices, indicative spend',
    },
    ...(isAdmin && data.per_org_economics ? [{
      id: 'engagement',
      label: 'ICER engagement',
      hint: 'ICER cashflow + cost of engagement (platform admin only)',
    }] : []),
  ]
  const [subTab, setSubTab] = useState('value')

  return (
    <>
      {/* ---- Sub-tab nav strip — subtler than the main tabs above ---- */}
      <div className="border-b border-sand-200 -mt-1 mb-1">
        <div className="flex flex-wrap gap-1">
          {SUB_TABS.map((t) => {
            const active = subTab === t.id
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSubTab(t.id)}
                title={t.hint}
                className={
                  'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ' +
                  (active
                    ? 'border-wantok-600 text-wantok-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300')
                }
              >
                {t.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* ---- Value & ROI ------------------------------------------------- */}
      {subTab === 'value' && <ValueROITab data={data} />}

      {/* ---- Workforce -------------------------------------------------- */}
      {subTab === 'workforce' && <WorkforceTab data={data} />}

      {/* ---- Clinical activity ----------------------------------------- */}
      {subTab === 'clinical' && <ClinicalActivityTab data={data} />}

      {/* ---- Spend & catalogue ----------------------------------------- */}
      {subTab === 'spend' && <SpendTab data={data} />}

      {/* ---- ICER engagement (platform admin only) --------------------- */}
      {subTab === 'engagement' && isAdmin && data.per_org_economics && (
        <ICEREngagementTab eco={data.per_org_economics} />
      )}

      {/* ---- Footer (always shown) ------------------------------------- */}
      <div className="text-[11px] text-slate-500 italic">
        Computed {data.computed_at ? new Date(data.computed_at).toLocaleString() : ''} ·
        Privacy floor k={data.privacy_floor_k} ·
        Aggregate-only — individual records never returned.
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Sub-tab: Value & ROI
//
// The answer to "are we getting value?". Leads with the executive
// statement + savings tiles, then composition, then methodology.
// ---------------------------------------------------------------------------
function ValueROITab({ data }) {
  return (
    <>
      {/* Phase 6.5 — Subscription paid vs value delivered story.
          This is the new headline section: a direct money-in vs
          money-out comparison Issac wants on every org dashboard,
          using real prices (not avoided-visit proxies). */}
      <SubscriptionVsValueSection
        subscription={data.subscription_received}
        activity={data.activity_value_delivered}
        manual={data.icer_value_delivered}
        windowDays={data.window_days}
      />
      {/* Legacy telehealth-proxy "value impact" section removed 2026-06-11:
          it showed a net-of-subscription figure (often negative) that
          counted only telehealth savings, contradicting the complete
          Phase 6.5 value story above. The dashboard now tells ONE coherent
          value narrative. The legacy `value_impact` payload is still
          computed server-side (harmless) but no longer rendered. */}
    </>
  )
}

/**
 * Phase 6.5 — Subscription vs Value Delivered section.
 *
 * Renders three things in one card:
 *   1. Headline tiles: subscription received (lifetime + window) vs
 *      value delivered (window) plus the ROI ratio.
 *   2. Breakdown table: auto-priced activity rows + manual cost-entry
 *      rows + grand total.
 *   3. Transparent footer: data gaps for the four service types not
 *      yet captured as first-class platform events.
 *
 * Audience: visible to BOTH org admins and platform admins. The data
 * is positioned as "value ICER delivered to your organisation" — not
 * hidden ICER cost. Platform-wide overhead (rows with NULL org on the
 * cost-entry side) is filtered out server-side, so this view is safe
 * to show across the visibility line.
 */
function SubscriptionVsValueSection({ subscription, activity, manual, windowDays }) {
  // Tolerate missing fields — early-stage orgs may have no payments yet
  // and no value records.
  const sub = subscription || {}
  const act = activity || { by_service: [], total_value_pgk: 0 }
  const man = manual || { by_category: [], total_value_pgk: 0 }

  const subLifetime = Number(sub.lifetime_pgk ?? 0)
  const subWindow = Number(sub.window_pgk ?? 0)
  const expectedAnnual = sub.expected_annual_pgk != null
    ? Number(sub.expected_annual_pgk) : null
  // Pro-rated subscription for the active window — the FAIR
  // denominator for window-scoped ROI. E.g. K98,000/yr × (30/365) =
  // K8,054 is what NYDA's subscription "covers" in a 30-day window.
  const expectedForWindow = sub.expected_for_window_pgk != null
    ? Number(sub.expected_for_window_pgk) : null

  const autoTotal = Number(act.total_value_pgk ?? 0)
  const manualTotal = Number(man.total_value_pgk ?? 0)
  const valueTotal = autoTotal + manualTotal

  // ROI compares value delivered in this window against the
  // PRO-RATED subscription for the same window — like-for-like.
  // If pro-rated isn't available (no rate / no annual fee on file),
  // fall back to lifetime subscription so we still show something
  // useful for orgs in early-stage configuration.
  const roiDenominator = expectedForWindow != null && expectedForWindow > 0
    ? expectedForWindow
    : subLifetime
  const roi = roiDenominator > 0 ? valueTotal / roiDenominator : null
  const roiBasis = expectedForWindow != null && expectedForWindow > 0
    ? `${windowDays}-day pro-rata`
    : 'lifetime subscription'

  const fmtK = (n) => `K ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`

  return (
    <Card>
      <CardHeader
        title="Subscription & Value Delivered"
        subtitle={
          <>
            Compares what this organisation has paid in subscriptions
            against the monetary <em>value</em> of the health services
            ICER has delivered to their staff. No service is invoiced
            individually — orgs only pay a subscription. The breakdown
            below prices each delivered service at the rate-card so
            both sides can see what the subscription buys.
            {' '}<strong>{windowDays}-day window</strong> unless labelled
            "lifetime".
          </>
        }
      />

      {/* Headline tiles. The lifetime tile's subtitle computes the
          delta vs the contracted annual baseline so ad-hoc top-ups
          (positive delta) and arrears (negative delta) are visible at
          a glance. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile
          label="Subscription paid (lifetime)"
          value={fmtK(subLifetime)}
          subtitle={(() => {
            const paymentsLine = `${sub.payment_count ?? 0} payments to date`
            if (expectedAnnual == null) return paymentsLine
            const delta = subLifetime - expectedAnnual
            if (Math.abs(delta) < 0.005) return `${paymentsLine} · matches contract`
            if (delta > 0)
              return `${paymentsLine} · K${fmtK(delta).replace('K ','')} above annual contract`
            return `${paymentsLine} · K${fmtK(-delta).replace('K ','')} below annual contract`
          })()}
          colour="emerald"
        />
        <Tile
          label={`Subscription pro-rata (${windowDays}d)`}
          value={expectedForWindow != null ? fmtK(expectedForWindow) : '—'}
          subtitle={
            expectedAnnual != null
              ? `annual K${fmtK(expectedAnnual).replace('K ','').replace(',','')} × ${windowDays}/365`
              : 'no annual rate set'
          }
          colour="sky"
        />
        <Tile
          label={`Value delivered (${windowDays}d)`}
          value={fmtK(valueTotal)}
          subtitle={`auto + manual`}
          colour="amber"
        />
        <Tile
          label="ROI (value / pro-rata)"
          value={roi != null ? `${roi.toFixed(2)}×` : '—'}
          subtitle={
            roi == null
              ? 'no comparator yet'
              : roi >= 1
                ? `value exceeds ${roiBasis}`
                : `${(roi * 100).toFixed(0)}% of ${roiBasis} delivered`
          }
          colour={roi != null && roi >= 1 ? 'emerald' : 'slate'}
        />
      </div>

      {/* Expected-annual disclosure */}
      {expectedAnnual != null && (
        <div className="text-xs text-slate-600 italic">
          Negotiated annual subscription: {fmtK(expectedAnnual)} ·{' '}
          {sub.active_member_count ?? 0} active members ·{' '}
          ad-hoc top-up payments tracked separately.
        </div>
      )}

      {/* Breakdown table */}
      <div className="space-y-3">
        {/* Auto-priced section */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-1">
            Auto-priced from activity ({windowDays}d window)
          </div>
          <table className="w-full text-sm">
            <tbody>
              {(act.by_service || []).map((row) => (
                <tr key={row.service_type} className="border-b border-sand-100">
                  <td className="py-1.5 pr-3">{prettyServiceLabel(row.service_type)}</td>
                  <td className="py-1.5 pr-3 text-slate-500 text-xs">
                    {row.count} {row.count === 1 ? 'event' : 'events'}
                    {row.markup_pct ? ` · +${row.markup_pct}% markup` : ''}
                    {row.underlying_cost_pgk != null
                      ? ` on K ${Number(row.underlying_cost_pgk).toFixed(2)} cost`
                      : ''}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {fmtK(row.value_pgk)}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-slate-300 font-medium">
                <td className="py-1.5 pr-3">Auto subtotal</td>
                <td></td>
                <td className="py-1.5 text-right tabular-nums">{fmtK(autoTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* External-provider activity — delivered by a NON-ICER pharmacy / lab /
            imaging centre, so it is billed to the patient separately and ICER
            recognises only the consultation fee. Shown for transparency; it is
            NOT part of value delivered. */}
        {(act.billed_separately || []).length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-1">
              Billed separately — consultation fee only ({windowDays}d window)
            </div>
            <table className="w-full text-sm">
              <tbody>
                {(act.billed_separately || []).map((row) => (
                  <tr key={`ext-${row.service_type}`} className="border-b border-sand-100 text-slate-500">
                    <td className="py-1.5 pr-3">
                      {prettyServiceLabel(row.service_type)} (external provider)
                    </td>
                    <td className="py-1.5 pr-3 text-xs">
                      {row.count} {row.count === 1 ? 'event' : 'events'} · not covered by subscription
                    </td>
                    <td className="py-1.5 text-right tabular-nums">{fmtK(row.value_pgk)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[11px] text-slate-500 italic mt-1">
              Delivered by non-ICER providers and billed to the patient directly —
              excluded from the value delivered above.
            </div>
          </div>
        )}

        {/* Manual cost-entry section */}
        {(man.by_category || []).length > 0 ? (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-1">
              Manual entries by ICER team ({windowDays}d window)
            </div>
            <table className="w-full text-sm">
              <tbody>
                {(man.by_category || []).map((row) => (
                  <tr key={row.category} className="border-b border-sand-100">
                    <td className="py-1.5 pr-3">{row.category_label}</td>
                    <td className="py-1.5 text-right tabular-nums">
                      {fmtK(row.total_pgk)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-slate-300 font-medium">
                  <td className="py-1.5 pr-3">Manual subtotal</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtK(manualTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-xs text-slate-500 italic">
            No manual entries yet — ICER team activity (site visits, meals,
            high-risk identification, management recommendations) will appear
            here once recorded via the cost-entry surface.
          </div>
        )}

        {/* Grand total */}
        <div className="border-t-2 border-slate-400 pt-2 flex justify-between font-semibold">
          <span>Total value delivered ({windowDays}d window)</span>
          <span className="tabular-nums">{fmtK(valueTotal)}</span>
        </div>
      </div>

      {/* Data gaps footer */}
      {(act.data_gaps || []).length > 0 && (
        <div className="text-[11px] text-slate-500 italic border-t border-sand-200 pt-2">
          <strong className="not-italic">Not yet auto-priced:</strong>{' '}
          {act.data_gaps.map((g, i) => (
            <span key={g.service_type}>
              {i > 0 && ' · '}
              {prettyServiceLabel(g.service_type)}
            </span>
          ))}
          {'. '}
          These items are either captured manually via the cost-entry
          surface or pending a future data-capture enhancement.
        </div>
      )}
    </Card>
  )
}

/** Friendly label for ServiceRate.service_type slugs. */
function prettyServiceLabel(slug) {
  return {
    platform_fee:     'Platform access fee',
    online_consult:   'Online consultations',
    medical_exam:     'Medical examinations',
    site_visit:       'Site visits',
    home_visit:       'Home visits',
    outreach:         'Outreach visits',
    medication:       'Medications',
    lab:              'Lab tests',
    radiology:        'Radiology',
    ambulance:        'Ambulance',
    medical_report:   'Medical reports',
    opportunity_cost: 'Clinic closure opportunity cost',
  }[slug] || slug
}

// ---------------------------------------------------------------------------
// Sub-tab: Workforce
//
// Who is enrolled, what does their demography look like, what chronic
// conditions are coded against them. Headline tiles first, then
// demographics, then chronic disease prevalence.
// ---------------------------------------------------------------------------
function WorkforceTab({ data }) {
  const h = data.headline || {}
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label="Total staff" value={h.staff_total} colour="slate" />
        <Tile label="Active" value={h.staff_active} colour="emerald" />
        <Tile label="Inactive" value={h.staff_inactive} colour="slate" />
        <Tile
          label="Seat utilisation"
          value={h.seat_utilisation_pct != null ? `${h.seat_utilisation_pct}%` : '—'}
          subtitle={h.seat_limit ? `of ${h.seat_limit} seats` : 'no seat limit'}
          colour="sky"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <BreakdownCard title="Sex" buckets={data.demographics?.sex} k={data.privacy_floor_k} />
        <BreakdownCard title="Age" buckets={data.demographics?.age_bands} k={data.privacy_floor_k} />
        <BreakdownCard title="Departments" buckets={data.demographics?.departments} k={data.privacy_floor_k} />
      </div>

      <Card>
        <CardHeader
          title="Chronic disease prevalence (lifetime)"
          subtitle="Percentage of active staff with a coded ICD-10 diagnosis. Buckets below the privacy floor are suppressed."
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {(data.chronic_conditions || []).length === 0 ? (
            <Empty />
          ) : (
            (data.chronic_conditions || []).map((c) => (
              <ChronicTile key={c.key} label={c.label} v={c} />
            ))
          )}
        </div>
      </Card>

      <MeasuredRiskCard data={data} />
    </>
  )
}

// ---------------------------------------------------------------------------
// Sub-tab: Clinical activity
//
// What happened in the analytics window — encounters, prescriptions,
// labs, imaging, ambulance. Five cards arranged in a 1+2x2 grid.
// ---------------------------------------------------------------------------
function ClinicalActivityTab({ data }) {
  return (
    <>
      <Card>
        <CardHeader
          title={`Encounters · last ${data.window_days} days`}
          subtitle={
            data.encounters?.avg_per_active_staff != null
              ? `Total ${data.encounters.total} · avg ${data.encounters.avg_per_active_staff} per active staff`
              : `Total ${data.encounters?.total ?? 0}`
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Breakdown title="By consultation type" buckets={data.encounters?.by_type} k={data.privacy_floor_k} />
          <Breakdown title="By status" buckets={data.encounters?.by_status} k={data.privacy_floor_k} />
        </div>
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            Top diagnoses (ICD-10) · k≥{data.privacy_floor_k}
          </div>
          {!data.encounters?.top_diagnoses?.length ? (
            <Empty />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="text-left p-2 w-24">Code</th>
                  <th className="text-left p-2">Description</th>
                  <th className="text-right p-2 w-20">Count</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.encounters.top_diagnoses.map((d, i) => (
                  <tr key={i}>
                    <td className="p-2 font-mono text-wantok-700">{d.code}</td>
                    <td className="p-2">{d.description}</td>
                    <td className="p-2 text-right font-semibold">{d.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <CardHeader
            title="Prescriptions"
            subtitle={`${data.prescriptions?.total ?? 0} total · ${data.prescriptions?.controlled_substance_count ?? 0} controlled-substance lines`}
          />
          <Breakdown title="Top drugs" buckets={data.prescriptions?.top_drugs} k={data.privacy_floor_k} />
        </Card>
        <Card>
          <CardHeader title="Lab tests" subtitle={`${data.labs?.total ?? 0} total`} />
          <Breakdown title="Top tests" buckets={data.labs?.top_tests} k={data.privacy_floor_k} />
        </Card>
        <Card>
          <CardHeader title="Imaging studies" subtitle={`${data.imaging?.total ?? 0} total`} />
          {(data.imaging?.total ?? 0) === 0 ? (
            <div className="text-xs text-slate-400 italic">
              No imaging studies this period.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Breakdown title="Top studies" buckets={data.imaging?.top_studies} k={data.privacy_floor_k} />
              <Breakdown title="By modality" buckets={data.imaging?.by_modality} k={data.privacy_floor_k} />
            </div>
          )}
        </Card>
        <Card>
          <CardHeader title="Ambulance requests" subtitle={`${data.ambulance?.total ?? 0} total`} />
          {(data.ambulance?.total ?? 0) === 0 ? (
            <div className="text-xs text-slate-400 italic">
              No ambulance requests this period.
            </div>
          ) : (
            <Breakdown title="By urgency" buckets={data.ambulance?.by_urgency} k={data.privacy_floor_k} />
          )}
        </Card>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Sub-tab: Spend & catalogue
//
// What the platform costs the org and what services are priced at.
// Subscription editor (platform-admin write surface), service catalogue
// (per-org contracted rates), then indicative spend baseline.
// ---------------------------------------------------------------------------
function SpendTab({ data }) {
  return (
    <>
      <SubscriptionPanel
        orgId={data.organization?.id}
        orgName={data.organization?.name}
        windowDays={data.window_days}
        annualSubscription={
          data.value_impact?.inputs?.annual_subscription_pgk
        }
        proRatedDenominator={
          data.value_impact?.inputs?.indicative_spend_pgk
        }
        basis={data.value_impact?.inputs?.program_cost_basis}
      />

      <ServicePriceCatalogue prices={data.service_prices || []} />

      <Card>
        <CardHeader
          title="Indicative spend"
          subtitle={`Platform-baseline cost of prescriptions, labs and imaging in the last ${data.window_days} days. Real cost varies by pharmacy/lab/centre pricing.`}
        />
        <div className="text-2xl font-display font-semibold text-slate-900">
          K {Number(data.indicative_spend_pgk || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </Card>
    </>
  )
}

// ---------------------------------------------------------------------------
// Sub-tab: ICER engagement (Phase 6.3 — platform admin only)
//
// The cashflow + nation-building story for ICER's relationship with
// THIS specific org. Two-way money flow narrative + cost breakdown
// attributable to this org + social-return ratio framing.
//
// Visibility: rendered only when isAdmin && data.per_org_economics is
// present. The backend additionally omits per_org_economics from the
// response when the requester isn't is_staff/is_superuser, so even if a
// determined non-admin user manually flipped the FE flag they'd see
// an empty object.
// ---------------------------------------------------------------------------
function ICEREngagementTab({ eco }) {
  const fmtPGK = (n) => Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })
  const fmtNum = (n, dp = 0) => Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: dp, maximumFractionDigits: dp,
  })
  const cats = eco.costs_by_category || []
  const services = eco.costs_by_service || []
  const totalCost = eco.costs_out_pgk || 0
  const breakdown = eco.costs_breakdown || {}
  const manualPart = Number(breakdown.manual_pgk || 0)
  const activityPart = Number(breakdown.activity_pgk || 0)
  const orgName = eco.organization?.name || 'this organisation'

  return (
    <>
      <Card>
        <CardHeader
          title={`ICER engagement · ${orgName} · last ${eco.window_days} days`}
          subtitle={`Cashflow between ICER and ${orgName}. Money in is the subscription paid by ${orgName}. Money out is the total value of services ICER delivered — auto-priced from real activity (consultations, prescriptions, lab/imaging, reports) at ICER's rate-card, plus any manually-entered operational costs attributable to ${orgName} (site visits, meals, high-risk identification). Net margin = subscription − value delivered. Platform-admin view only.`}
          right={
            <span className="text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 bg-slate-100 text-slate-700 border-slate-300">
              ICER internal
            </span>
          }
        />

        {eco.statement && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-800 leading-relaxed">
            {eco.statement}
          </div>
        )}

        {/* ---- Cashflow tiles — 4 columns telling a left-to-right story ---- */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          <Tile
            label="Money in · subscription"
            value={`PGK ${fmtPGK(eco.revenue_in_pgk)}`}
            subtitle={`${orgName} → ICER`}
            colour="emerald"
          />
          <Tile
            label="Money out · value delivered"
            value={`PGK ${fmtPGK(eco.costs_out_pgk)}`}
            subtitle={
              (manualPart > 0 || activityPart > 0)
                ? `auto K${fmtPGK(activityPart)} + manual K${fmtPGK(manualPart)}`
                : `ICER → ${orgName} program`
            }
            colour="rose"
          />
          <Tile
            label="Net margin to ICER"
            value={`PGK ${fmtPGK(eco.net_margin_pgk)}`}
            subtitle={
              eco.margin_pct != null
                ? `${fmtNum(eco.margin_pct, 0)}% of revenue`
                : 'no revenue yet'
            }
            colour={(eco.net_margin_pgk || 0) >= 0 ? 'sky' : 'rose'}
          />
          <Tile
            label="Value delivered to org"
            value={`PGK ${fmtPGK(eco.value_delivered_pgk)}`}
            subtitle={
              eco.social_return_ratio != null
                ? `${fmtNum(eco.social_return_ratio, 2)}x social return`
                : 'no cost basis yet'
            }
            colour="amber"
          />
        </div>

        {/* ---- Cost breakdown attributable to this org ---- */}
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
            ICER costs attributed to {orgName}
          </div>
          {cats.length === 0 ? (
            <div className="text-xs text-slate-400 italic">
              No costs have been attributed to {orgName} in this window.
              Add cost entries at{' '}
              <a
                href="/superuser/platform-costs"
                className="underline text-wantok-700 hover:text-wantok-800"
              >
                /superuser/platform-costs
              </a>
              {' '}and set their "Attributable to org" field to {orgName}.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {cats.map((c, i) => {
                const pct = totalCost > 0 ? (c.total_pgk / totalCost) * 100 : 0
                return (
                  <li key={i} className="text-sm">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-slate-700 truncate">
                        {c.category_label}
                        {c.hours != null && (
                          <span className="text-[11px] text-slate-400 ml-2">
                            · {fmtNum(c.hours, 1)} hrs
                          </span>
                        )}
                      </span>
                      <span className="font-semibold tabular-nums text-slate-900 shrink-0">
                        K{fmtPGK(c.total_pgk)}
                        <span className="text-[11px] text-slate-500 ml-2">
                          {fmtNum(pct, 0)}%
                        </span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                      <div
                        className="h-full bg-rose-500 rounded-full"
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* ---- Auto-priced service breakdown ---- */}
        {services.length > 0 && (
          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
              Services delivered to {orgName} (auto-priced from activity)
            </div>
            <ul className="space-y-1.5">
              {services.map((s, i) => {
                const pct = totalCost > 0 ? (s.value_pgk / totalCost) * 100 : 0
                return (
                  <li key={s.service_type || i} className="text-sm">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-slate-700 truncate">
                        {prettyServiceLabel(s.service_type)}
                        <span className="text-[11px] text-slate-400 ml-2">
                          · {s.count} {s.count === 1 ? 'event' : 'events'}
                          {s.markup_pct ? ` · +${s.markup_pct}% markup` : ''}
                          {s.underlying_cost_pgk != null
                            ? ` on K${fmtPGK(s.underlying_cost_pgk)} cost`
                            : ''}
                        </span>
                      </span>
                      <span className="font-semibold tabular-nums text-slate-900 shrink-0">
                        K{fmtPGK(s.value_pgk)}
                        <span className="text-[11px] text-slate-500 ml-2">
                          {fmtNum(pct, 0)}%
                        </span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                      <div
                        className="h-full bg-amber-500 rounded-full"
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </li>
                )
              })}
            </ul>
            {(eco.data_gaps || []).length > 0 && (
              <div className="text-[11px] text-slate-500 italic mt-2">
                <strong className="not-italic">Not yet auto-priced:</strong>{' '}
                {eco.data_gaps.map((g, i) => (
                  <span key={g.service_type}>
                    {i > 0 && ' · '}
                    {prettyServiceLabel(g.service_type)}
                  </span>
                ))}
                {'. Captured via manual cost-entry until first-class.'}
              </div>
            )}
          </div>
        )}

        {/* ---- Nation-building framing ---- */}
        {(eco.social_return_ratio != null && eco.social_return_ratio > 0) && (
          <div className="mt-5 rounded-xl bg-gradient-to-r from-wantok-50 to-coral-50 border border-wantok-100 p-4 text-sm text-slate-800 leading-relaxed">
            <div className="text-[11px] uppercase tracking-wide text-wantok-700 font-semibold mb-1">
              Nation-building contribution
            </div>
            ICER invested <b>PGK {fmtPGK(eco.costs_out_pgk)}</b> of operational
            capacity in {orgName}&apos;s workforce health program this window,
            returning <b>PGK {fmtPGK(eco.value_delivered_pgk)}</b> in
            measurable health value to their staff — a{' '}
            <b>{fmtNum(eco.social_return_ratio, 2)}x social return</b> on
            ICER&apos;s investment in PNG public-sector workforce health.
          </div>
        )}

        {/* ---- Methodology footnote ---- */}
        <div className="mt-4 text-[11px] text-slate-500 leading-relaxed">
          <b>How this is calculated:</b> Money in is the pro-rated portion
          of {orgName}&apos;s annual subscription
          ({eco.inputs?.annual_subscription_pgk
            ? `PGK ${fmtPGK(eco.inputs.annual_subscription_pgk)}/yr × ${eco.window_days}/365`
            : 'not yet set'}).
          Money out is the sum of operating costs whose Attributable Org
          is {orgName}, captured via{' '}
          <a
            href="/superuser/platform-costs"
            className="underline hover:text-slate-700"
          >
            /superuser/platform-costs
          </a>. Social return is PGK of org-side savings (from the org&apos;s
          Value & ROI section) divided by PGK of ICER cost attributed.
        </div>
      </Card>
    </>
  )
}

// ---------------------------------------------------------------------------
// Tiny presentational helpers
// ---------------------------------------------------------------------------

function Tile({ label, value, subtitle, colour = 'slate' }) {
  const map = {
    slate: 'border-sand-200/60 bg-white',
    emerald: 'border-emerald-200 bg-emerald-50',
    sky: 'border-sky-200 bg-sky-50',
    amber: 'border-amber-200 bg-amber-50',
    rose: 'border-rose-200 bg-rose-50',
  }
  return (
    <div className={`border rounded-2xl px-4 py-3 ${map[colour]}`}>
      <div className="text-3xl font-display font-bold text-slate-900">{value ?? '—'}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">{label}</div>
      {subtitle && <div className="text-[11px] text-slate-400 mt-0.5">{subtitle}</div>}
    </div>
  )
}

function BreakdownCard({ title, buckets, k }) {
  return (
    <Card>
      <CardHeader title={title} subtitle={`k≥${k} privacy floor`} />
      <Breakdown buckets={buckets} k={k} />
    </Card>
  )
}

function Breakdown({ title, buckets, k }) {
  if (title) {
    return (
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{title}</div>
        <BreakdownInner buckets={buckets} k={k} />
      </div>
    )
  }
  return <BreakdownInner buckets={buckets} k={k} />
}

function BreakdownInner({ buckets, k }) {
  if (!buckets || buckets.length === 0) {
    return (
      <div className="text-xs text-slate-400 italic">
        No buckets meet the k≥{k} privacy floor.
      </div>
    )
  }
  const max = Math.max(...buckets.map((b) => b.count || 0), 1)
  return (
    <ul className="space-y-1">
      {buckets.map((b, i) => (
        <li key={i} className="text-xs">
          <div className="flex justify-between">
            <span className="text-slate-700">{b.label}</span>
            <span className="font-semibold tabular-nums text-slate-900">{b.count}</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-wantok-500"
              style={{ width: `${(b.count / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

function ChronicTile({ label, v }) {
  const suppressed = v?.suppressed
  return (
    <div className={`border rounded-xl px-3 py-2 ${suppressed ? 'border-slate-200 bg-slate-50' : 'border-amber-200 bg-amber-50'}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      {suppressed ? (
        <>
          <div className="text-sm font-medium text-slate-400 italic mt-0.5">suppressed</div>
          <div className="text-[10px] text-slate-400">{`k<${5}`}</div>
        </>
      ) : (
        <>
          <div className="text-lg font-semibold text-amber-900">{v?.pct ?? '—'}%</div>
          <div className="text-[11px] text-amber-700">{v?.count ?? 0} affected</div>
        </>
      )}
    </div>
  )
}

// Measured risk — derived from the latest recorded vitals per active member
// (BP banding + BMI banding), independent of coded diagnoses. Each band is
// k-anonymity suppressed server-side; we mirror the ChronicTile treatment.
function MeasuredRiskCard({ data }) {
  const mr = data.measured_risk
  if (!mr) return null
  const tiles = [
    ['Raised BP', mr.raised_bp],
    ['Hypertensive', mr.hypertensive],
    ['Overweight / obese', mr.overweight_or_obese],
    ['Obese', mr.obese],
    ['Raised BSL', mr.raised_bsl],
  ]
  return (
    <Card>
      <CardHeader
        title="Measured risk (latest recorded vitals)"
        subtitle="From each active member's most recent recorded BP and BMI — surfaces risk even before a diagnosis is coded. Bands below the privacy floor are suppressed."
      />
      {!mr.measured_n ? (
        <div className="text-xs text-slate-400 italic">No vitals recorded for this cohort yet.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {tiles.map(([label, v]) => (
              <MeasuredTile key={label} label={label} v={v} />
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-2">
            Based on {mr.measured_n} member{mr.measured_n === 1 ? '' : 's'} with recorded vitals.{mr.bsl_measured_n ? ` Raised BSL is of ${mr.bsl_measured_n} member${mr.bsl_measured_n === 1 ? '' : 's'} with a glucose reading.` : ''}
          </div>
        </>
      )}
    </Card>
  )
}

function MeasuredTile({ label, v }) {
  const suppressed = v?.suppressed
  return (
    <div className={`border rounded-xl px-3 py-2 ${suppressed ? 'border-slate-200 bg-slate-50' : 'border-sky-200 bg-sky-50'}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      {suppressed ? (
        <>
          <div className="text-sm font-medium text-slate-400 italic mt-0.5">suppressed</div>
          <div className="text-[10px] text-slate-400">{`k<${5}`}</div>
        </>
      ) : (
        <>
          <div className="text-lg font-semibold text-sky-900">{v?.pct_of_measured ?? '—'}%</div>
          <div className="text-[11px] text-sky-700">{v?.count ?? 0} of measured</div>
        </>
      )}
    </div>
  )
}

function Empty() {
  return <div className="text-xs text-slate-400 italic">No data in this window.</div>
}

/**
 * SubscriptionPanel — platform-admin-only inline editor for the org's
 * annual subscription fee. Shown above the Service price catalogue on
 * the Workforce health tab so the figure that drives the ROI
 * denominator is one click away. Org admins don't see this card; they
 * see the methodology disclosure inside Value & Impact for the same
 * data, read-only.
 *
 * Contract: subscription is a running annual figure. When OPIC pays
 * a top-up mid-year, platform admin updates it here — no schema
 * change, no shell access required. Future enhancement: dedicated
 * payment-ledger table that auto-sums into this value.
 */
function SubscriptionPanel({
  orgId, orgName, windowDays,
  annualSubscription, proRatedDenominator, basis,
}) {
  const { user } = useAuth()
  const isPlatformAdmin = Boolean(user?.is_staff || user?.is_superuser)
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saved, setSaved] = useState(false)

  // Hydrate the editor whenever the upstream value changes (e.g. after
  // a successful save). Empty string when null so the input renders
  // cleanly as a placeholder.
  useEffect(() => {
    setDraft(annualSubscription != null ? String(annualSubscription) : '')
  }, [annualSubscription])

  const mut = useMutation({
    mutationFn: () => setOrgAnnualSubscription(
      orgId,
      draft === '' ? null : draft,
    ),
    onSuccess: () => {
      setEditing(false)
      setSaved(true)
      // Refetch the dashboard so the ROI tile + methodology + denominator
      // all reflect the new figure.
      qc.invalidateQueries({ queryKey: ['org-dashboard', orgId] })
      qc.invalidateQueries({ queryKey: ['org-detail', orgId] })
      setTimeout(() => setSaved(false), 2500)
    },
  })

  // Hide entirely from non-admin viewers — they get the read-only
  // figure inside the Value & Impact methodology disclosure.
  if (!isPlatformAdmin) return null

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })

  const subtitle = annualSubscription != null
    ? `Drives the ROI denominator on this organisation's Value & Impact section, pro-rated to the active analytics window.`
    : `No subscription on file yet — the ROI tile is currently using the rendered-services baseline. Set the annual fee for a board-defensible figure.`

  return (
    <Card>
      <CardHeader
        title="Subscription &amp; billing"
        subtitle={subtitle}
        right={
          <span className="text-[10px] uppercase tracking-wide font-bold text-coral-700 bg-coral-50 border border-coral-200 rounded-full px-2 py-0.5">
            Platform admin
          </span>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        {/* Annual figure — primary, editable inline */}
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
            Annual subscription · {orgName || 'this organisation'}
          </div>
          {!editing ? (
            <div className="mt-1 flex items-baseline gap-3">
              <div className="text-3xl font-display font-semibold text-slate-900 tabular-nums">
                {annualSubscription != null
                  ? `K${fmt(annualSubscription)}`
                  : <span className="text-slate-400 italic text-xl">Not set</span>}
              </div>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs text-wantok-700 hover:text-wantok-900 font-semibold underline"
              >
                {annualSubscription != null ? 'Update' : 'Set fee'}
              </button>
              {saved && (
                <span className="text-emerald-700 text-xs font-medium">Saved ✓</span>
              )}
            </div>
          ) : (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="text-2xl font-bold text-slate-700">K</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="e.g. 75200.00"
                autoFocus
                className="border-2 border-wantok-300 focus:border-wantok-700 rounded-lg px-3 py-2 text-2xl font-display font-semibold tabular-nums w-56"
              />
              <button
                type="button"
                onClick={() => mut.mutate()}
                disabled={mut.isPending}
                className="text-sm font-semibold bg-wantok-700 hover:bg-wantok-800 text-white rounded px-4 py-2 disabled:opacity-60"
              >
                {mut.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false)
                  setDraft(annualSubscription != null ? String(annualSubscription) : '')
                }}
                disabled={mut.isPending}
                className="text-xs text-slate-500 hover:text-slate-800"
              >
                Cancel
              </button>
            </div>
          )}
          <div className="mt-2 text-[11px] text-slate-500 leading-snug">
            Update this any time additional payments arrive — the ROI
            calculation refreshes immediately. Leave blank to fall back
            to the rendered-services baseline (early-stage orgs without
            a contract on file).
          </div>
          {mut.isError && (
            <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
              {String(
                mut.error?.response?.data?.error?.message
                || mut.error?.response?.data?.detail
                || mut.error?.message
                || 'Could not save. Please try again.'
              )}
            </div>
          )}
        </div>

        {/* Pro-rated cost in the active window */}
        <div className="md:border-l md:border-slate-100 md:pl-4">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
            Pro-rated for last {windowDays} days
          </div>
          <div className="text-2xl font-display font-semibold text-slate-700 tabular-nums mt-1">
            K{fmt(proRatedDenominator)}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {basis === 'subscription_pro_rated'
              ? 'Annual fee × window / 365 — used as ROI denominator.'
              : 'Catalogue-priced rendered services — fallback while no annual fee is set.'}
          </div>
        </div>
      </div>
    </Card>
  )
}

/**
 * Per-org service-price catalogue — surfaces the contracted rates each
 * org has agreed for the standard services Wantok delivers. The same
 * format applies to every org; only the figures differ. Source badges
 * make it obvious which lines are this-org-specific contracted rates
 * vs platform defaults the org silently inherited.
 */
function ServicePriceCatalogue({ prices }) {
  if (!prices || prices.length === 0) return null
  // Group source labels for the legend.
  const overrideCount = prices.filter((p) => p.source === 'org_override').length
  const subtitle = overrideCount > 0
    ? `${overrideCount} of ${prices.length} services priced under this organisation's contract; the rest follow platform defaults.`
    : 'Currently inheriting platform-default prices on every service. Platform admin can set per-org contracted rates.'
  return (
    <Card>
      <CardHeader
        title="Service price catalogue"
        subtitle={subtitle}
      />
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-sm">
        {prices.map((p) => (
          <li
            key={p.code}
            className="flex items-center justify-between gap-3 border-b border-slate-50 py-1.5"
          >
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-800 truncate">{p.label}</div>
              <div className="text-[10px] uppercase tracking-wide mt-0.5">
                {p.source === 'org_override' ? (
                  <span className="text-wantok-700 font-bold">Contracted</span>
                ) : p.source === 'platform_default' ? (
                  <span className="text-slate-400">Platform default</span>
                ) : (
                  <span className="text-amber-700">Built-in fallback</span>
                )}
              </div>
            </div>
            <div className={
              `text-base font-bold tabular-nums ` +
              (p.source === 'org_override' ? 'text-wantok-800' : 'text-slate-900')
            }>
              K{Number(p.price_pgk).toLocaleString(undefined, {
                minimumFractionDigits: 2, maximumFractionDigits: 2,
              })}
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-3 text-[11px] text-slate-500 leading-snug">
        Contracted rates are set by the platform admin per organisation
        and apply across the Value & Impact engine — every encounter
        that delivers one of these services is valued at the rate above.
        Doctor-entered services (prescriptions, lab tests, imaging
        studies) carry their own per-item pricing from the respective
        catalogues.
      </div>
    </Card>
  )
}

function prettyName(s) {
  return s
    .replace(/_/g, ' ')
    .replace(/\bihd\b/gi, 'IHD')
    .replace(/\bckd\b/gi, 'CKD')
    .replace(/\bhiv\b/gi, 'HIV')
    .replace(/\btb\b/gi, 'TB')
    .replace(/\bcopd\b/gi, 'COPD')
    .replace(/^./, (c) => c.toUpperCase())
}

// ---------------------------------------------------------------------------
// Value & Impact section
//
// Translates platform activity into a board-defensible savings number.
// Lives above the workforce-health metrics on purpose: a CFO will read
// the headline ROI before drilling into staffing or chronic-disease data.
// ---------------------------------------------------------------------------
function ValueImpactSection({ statement, v, windowDays }) {
  const [showAssumptions, setShowAssumptions] = useState(false)

  if (!v) {
    return (
      <Card>
        <CardHeader title="Value & impact" subtitle="Awaiting data." />
        <Empty />
      </Card>
    )
  }

  const cohort = v.cohort || {}
  const inputs = v.inputs || {}

  const sourceLabel = {
    org_override:        { text: 'Per-org override',    cls: 'bg-wantok-50 text-wantok-800 border-wantok-200' },
    global_default:      { text: 'Platform default',    cls: 'bg-sky-50 text-sky-700 border-sky-200' },
    hardcoded_fallback:  { text: 'Built-in fallback',   cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  }[inputs.assumption_source] || { text: 'Unknown', cls: 'bg-slate-50 text-slate-600 border-slate-200' }

  // Composition of total savings — four pillars, narrated as percentages.
  const segments = [
    { label: 'Direct care',     value: v.direct_care_savings || 0,         cls: 'bg-wantok-600'  },
    { label: 'Travel avoided',  value: v.travel_savings || 0,              cls: 'bg-emerald-500' },
    { label: 'Productivity',    value: v.productivity_value || 0,          cls: 'bg-sky-500'     },
    { label: 'Admissions',      value: v.admission_avoidance_savings || 0, cls: 'bg-coral-500'   },
  ]
  const totalForBar = segments.reduce((s, x) => s + (x.value || 0), 0) || 1
  const pct = (n) => Math.round((n / totalForBar) * 100)

  // ROI presentation. When indicative spend is zero (early days) the
  // backend returns null — we show a friendly tag instead of "—%".
  const roiNum = v.roi_percent
  const roiText = roiNum == null ? null : `${fmtNum(roiNum, 0)}%`

  return (
    <Card>
      <CardHeader
        title="Workforce health value & impact"
        subtitle={`Trailing ${windowDays} days · k≥5 anonymity floor on every breakdown · figures are conservative estimates.`}
        right={
          <span className={`text-[10px] uppercase tracking-wide font-semibold border rounded-full px-2 py-0.5 ${sourceLabel.cls}`}>
            {sourceLabel.text}
          </span>
        }
      />

      {/* Executive hero — single statement framed as a board narrative,
          with the dominant number + ROI lifted out for at-a-glance read. */}
      <div className="rounded-2xl border border-wantok-100 bg-gradient-to-br from-wantok-50/80 via-white to-coral-50/60 p-5 mt-1">
        <div className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-start">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-wantok-700">
              Executive summary
            </div>
            {statement && (
              <p className="mt-2 text-[15px] leading-relaxed text-slate-800">
                {statement}
              </p>
            )}
          </div>
          <div className="lg:border-l lg:border-wantok-100 lg:pl-6 flex flex-row lg:flex-col items-start gap-6 lg:gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">
                Net value delivered
              </div>
              <div className="text-3xl font-display font-semibold text-slate-900 tabular-nums mt-0.5">
                K{fmtPGK(v.net_value)}
              </div>
              <div className="text-[11px] text-slate-500">after platform spend</div>
            </div>
            {roiText && (
              <div>
                <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">
                  Return on spend
                </div>
                <div className="text-3xl font-display font-semibold text-coral-700 tabular-nums mt-0.5">
                  {roiText}
                </div>
                <div className="text-[11px] text-slate-500">indicative ROI</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Three pillars. Each one tells a self-contained story — clinical
          care delivered / productivity recovered / risk and cost avoided.
          A board member can stop at any pillar and have a coherent read. */}
      <div className="mt-5 grid gap-4 lg:grid-cols-3">

        {/* Pillar 1 — Clinical care delivered */}
        <Pillar
          title="Clinical care delivered"
          subtitle="Encounters and clinical activity in this window."
        >
          <PillarStat
            value={cohort.encounters_total ?? 0}
            unit=""
            label="Encounters completed"
            note={
              `${cohort.telehealth_encounters ?? 0} telehealth · ` +
              `${cohort.physical_encounters ?? 0} in-person`
            }
          />
          <PillarStat
            value={`K${fmtPGK(v.direct_care_savings)}`}
            unit=""
            label="Direct-care savings"
            note="vs. equivalent traditional clinic visits"
          />
          <PillarStat
            value={v.avoided_visits ?? 0}
            unit=""
            label="Physical visits avoided"
            note="staff stayed productive at site"
          />
        </Pillar>

        {/* Pillar 2 — Workforce productivity recovered */}
        <Pillar
          title="Productivity recovered"
          subtitle="Hours and travel preserved by delivering care remotely."
        >
          <PillarStat
            value={fmtNum(v.time_saved_hours, 0)}
            unit="hrs"
            label="Staff hours saved"
            note="time not lost to clinic trips"
          />
          <PillarStat
            value={`K${fmtPGK(v.productivity_value)}`}
            unit=""
            label="Productivity value"
            note="hours × hourly productivity rate"
          />
          <PillarStat
            value={`K${fmtPGK(v.travel_savings)}`}
            unit=""
            label="Travel avoided"
            note="flights, fuel, and ground transport"
          />
        </Pillar>

        {/* Pillar 3 — Risk and cost avoided */}
        <Pillar
          title="Risk &amp; cost avoided"
          subtitle="Severe events caught early or kept out of hospital."
        >
          <PillarStat
            value={cohort.estimated_admissions_avoided ?? 0}
            unit=""
            label="Admissions estimated avoided"
            note="from high-risk encounters detected early"
          />
          <PillarStat
            value={`K${fmtPGK(v.admission_avoidance_savings)}`}
            unit=""
            label="Admission cost avoided"
            note="conservative — emergency-admission baseline"
          />
          <PillarStat
            value={`K${fmtPGK(v.total_savings)}`}
            unit=""
            label="Total savings"
            note="sum of all four savings categories"
          />
        </Pillar>
      </div>

      {/* Composition of savings — stacked bar with narrative percentages. */}
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-600 mb-2">
          Composition of total savings
        </div>
        <div className="h-3 w-full rounded-full overflow-hidden flex bg-slate-100">
          {segments.map((s, i) => (
            <div
              key={i}
              className={s.cls}
              style={{ width: `${(s.value / totalForBar) * 100}%` }}
              title={`${s.label}: PGK ${fmtPGK(s.value)}`}
            />
          ))}
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 mt-3 text-xs">
          {segments.map((s, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className={`inline-block h-2.5 w-2.5 rounded-full ${s.cls}`} />
              <span className="text-slate-600 flex-1 truncate">{s.label}</span>
              <span className="font-semibold tabular-nums text-slate-900">
                K{fmtPGK(s.value)}
              </span>
              <span className="text-slate-400 tabular-nums w-9 text-right">
                {pct(s.value)}%
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Methodology disclosure — collapsed by default. Includes the
          spend used as ROI denominator and every input value the
          calculator drew on, so the figure is fully defensible. */}
      <div className="mt-5 border-t border-slate-100 pt-3">
        <button
          type="button"
          className="text-xs text-wantok-700 hover:text-wantok-900 font-semibold"
          onClick={() => setShowAssumptions((s) => !s)}
        >
          {showAssumptions ? '▾ Hide methodology' : '▸ Methodology & assumptions'}
        </button>
        {showAssumptions && (
          <div className="mt-3 space-y-3">
            <div className="text-[11px] text-slate-500 leading-relaxed">
              All figures are conservative estimates derived from platform
              activity in the trailing {windowDays}-day window. Per-org
              overrides take precedence; in their absence the platform-default
              values below apply. Privacy: every demographic and clinical
              breakdown elsewhere on this page is suppressed when the
              underlying cohort is smaller than {5} staff (k≥5 floor).
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs text-slate-700">
              <Assumption k="Traditional visit cost"     v={`K${fmtPGK(inputs.avg_traditional_visit_cost_pgk)}`} />
              <Assumption k="Telehealth consult cost"    v={`K${fmtPGK(inputs.avg_telehealth_consult_cost_pgk)}`} />
              <Assumption k="Travel cost / visit"        v={`K${fmtPGK(inputs.avg_travel_cost_per_visit_pgk)}`} />
              <Assumption k="Emergency admission cost"   v={`K${fmtPGK(inputs.avg_emergency_admission_cost_pgk)}`} />
              <Assumption k="Hours lost / visit"         v={`${fmtNum(inputs.avg_hours_lost_per_visit, 1)} h`} />
              <Assumption k="Hourly productivity"        v={`K${fmtPGK(inputs.avg_hourly_productivity_pgk)}`} />
              <Assumption k="% telehealth avoiding visit"     v={`${fmtNum(inputs.pct_telehealth_avoiding_physical, 0)}%`} />
              <Assumption k="% high-risk admissions prevented" v={`${fmtNum(inputs.pct_high_risk_prevented, 0)}%`} />
              {inputs.annual_subscription_pgk != null && (
                <Assumption
                  k="Annual subscription (full year)"
                  v={`K${fmtPGK(inputs.annual_subscription_pgk)}`}
                />
              )}
              <Assumption
                k={
                  inputs.program_cost_basis === 'subscription_pro_rated'
                    ? `ROI denominator (subscription, ${windowDays}-day pro-rate)`
                    : 'ROI denominator (rendered-services baseline)'
                }
                v={`K${fmtPGK(inputs.indicative_spend_pgk)}`}
              />
              <Assumption
                k="Catalogue-price services rendered"
                v={`K${fmtPGK(inputs.services_rendered_pgk)}`}
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * One of the three "pillar" columns in the executive layout. Title +
 * subtitle frame the story; children are 3 PillarStat rows.
 */
function Pillar({ title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white/70 p-4">
      <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-wantok-800">
        {title}
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5 mb-3 leading-snug">
        {subtitle}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

/**
 * One number-led row inside a Pillar. Big number + label + small note.
 * Tabular-nums so columns align between rows.
 */
function PillarStat({ value, unit, label, note }) {
  return (
    <div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-display font-semibold text-slate-900 tabular-nums leading-none">
          {value}
        </span>
        {unit && (
          <span className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">
            {unit}
          </span>
        )}
      </div>
      <div className="text-xs font-semibold text-slate-700 mt-1">{label}</div>
      {note && <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{note}</div>}
    </div>
  )
}

function Assumption({ k, v }) {
  return (
    <div className="flex justify-between gap-2 border-b border-slate-50 py-1">
      <span className="text-slate-500">{k}</span>
      <span className="font-semibold tabular-nums text-slate-800">{v}</span>
    </div>
  )
}

function fmtPGK(n) {
  const num = Number(n || 0)
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function fmtNum(n, dp = 0) {
  const num = Number(n || 0)
  return num.toLocaleString(undefined, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })
}

// ===========================================================================
// Task #16 — Operational toolkit: enrolled members list + co-admins +
// bulk roster import. Lives between the self-enroll code card and the
// analytics body so HR-style "act on this" UI clusters together at the
// top of the page.
// ===========================================================================

// ---------------------------------------------------------------------------
// Enrolled members card — the org admin can finally see who's enrolled,
// not just the count. Light edits inline (department / role / employee_id /
// active toggle); destructive remove is a soft-delete (server flips
// is_active off rather than hard-deleting, preserving the encounter trail).
// ---------------------------------------------------------------------------
function EnrolledMembersCard({ orgId }) {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [activeOnly, setActiveOnly] = useState(true)
  const debounced = useDebounce(search, 250)

  const membersQ = useQuery({
    queryKey: ['org-members', orgId, debounced, activeOnly],
    queryFn: () => listOrgStaffLinks({
      orgId,
      search: debounced,
      isActive: activeOnly ? true : undefined,
    }),
    enabled: Boolean(orgId),
  })

  const rows = membersQ.data || []
  const subtitle = debounced
    ? `${rows.length} match${rows.length === 1 ? '' : 'es'} for "${debounced}".`
    : `${rows.length} ${activeOnly ? 'active' : 'total'} member${rows.length === 1 ? '' : 's'}.`

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['org-members', orgId] })
    qc.invalidateQueries({ queryKey: ['org-detail', orgId] })  // enrolled_count tile
  }

  return (
    <Card>
      <CardHeader
        title="Enrolled members"
        subtitle={subtitle}
        right={
          <button onClick={() => membersQ.refetch()} className="text-xs text-wantok-700 underline">
            Refresh
          </button>
        }
      />
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by name, employee ID, role, department…"
          className="flex-1 min-w-[14rem] border rounded px-3 py-2 text-sm"
        />
        <label className="text-xs text-slate-600 flex items-center gap-1.5 whitespace-nowrap">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          Active only
        </label>
      </div>

      {membersQ.error && <ErrorBanner error={membersQ.error} onRetry={() => membersQ.refetch()} />}

      {membersQ.isLoading ? (
        <div className="text-sm text-slate-400">Loading members…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-400 italic">
          {debounced
            ? `No active members match "${debounced}".`
            : 'No members enrolled yet. Distribute the self-enrollment code, or upload a CSV roster below.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
              <tr>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Employee ID</th>
                <th className="text-left p-2">Department</th>
                <th className="text-left p-2">Role</th>
                <th className="text-center p-2">Active</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((m) => (
                <MemberRow key={m.id} member={m} onChanged={invalidate} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function MemberRow({ member, onChanged }) {
  const [employeeId, setEmployeeId] = useState(member.employee_id || '')
  const [department, setDepartment] = useState(member.department || '')
  const [role, setRole] = useState(member.role || '')
  const [active, setActive] = useState(Boolean(member.is_active))

  const dirty = (
    employeeId !== (member.employee_id || '') ||
    department !== (member.department || '') ||
    role !== (member.role || '') ||
    active !== Boolean(member.is_active)
  )

  const save = useMutation({
    mutationFn: () => updateOrgStaffLink(member.id, {
      employee_id: employeeId,
      department,
      role,
      is_active: active,
    }),
    onSuccess: onChanged,
  })

  const remove = useMutation({
    mutationFn: () => removeOrgStaffLink(member.id),
    onSuccess: onChanged,
  })

  return (
    <tr className={dirty ? 'bg-amber-50/40' : ''}>
      <td className="p-2">
        <div className="font-medium text-slate-800">
          {member.patient_name || '(no name)'}
        </div>
        <div className="text-[11px] text-slate-500 font-mono">{member.patient_mrn}</div>
      </td>
      <td className="p-2">
        <input
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          className="w-28 border rounded px-2 py-1 text-xs"
          placeholder="—"
        />
      </td>
      <td className="p-2">
        <input
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          className="w-48 border rounded px-2 py-1 text-xs"
          placeholder="—"
          title={department || 'Set the staff member’s department'}
        />
      </td>
      <td className="p-2">
        <input
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="w-40 border rounded px-2 py-1 text-xs"
          placeholder="—"
          title={role || 'Set the staff member’s role'}
        />
      </td>
      <td className="p-2 text-center">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />
      </td>
      <td className="p-2 text-right whitespace-nowrap">
        <button
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
          className="text-xs bg-wantok-700 hover:bg-wantok-800 text-white rounded px-2 py-1 disabled:opacity-40"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={() => {
            if (window.confirm(
              `Remove ${member.patient_name || 'this member'} from the organisation? ` +
              `Their account stays — they're just unlinked from your org. Their ` +
              `historical encounters remain on their record.`
            )) {
              remove.mutate()
            }
          }}
          disabled={remove.isPending}
          className="ml-2 text-xs text-rose-600 hover:underline disabled:opacity-40"
        >
          Remove
        </button>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Co-administrators card — current admin links + pending invitations
// + an invite form. Org admins can grow their HR / wellness team
// without the platform admin in the loop, but the actual AdminLink
// ---------------------------------------------------------------------------
// Centres card — multi-site customers (OPIC plantations, NYDA regional
// offices, NSO field bureaux, NFA stations, PNG Customs ports, etc.)
// manage per-centre rows here. Each centre has its own self-enrolment
// code + member count + shareable /register URL.
// ---------------------------------------------------------------------------
function CentresCard({ orgId }) {
  const qc = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [draft, setDraft] = useState({ name: '', location: '', address: '' })
  const [copiedId, setCopiedId] = useState(null)
  const [justCreated, setJustCreated] = useState(null)

  // The owning organisation — surfaced in the form, the confirm dialog and
  // the success toast so a centre can't be filed under the wrong tenant by
  // mistake. Reuses the cached ['org-detail', orgId] query.
  const orgDetailQ = useQuery({
    queryKey: ['org-detail', orgId],
    queryFn: () => getOrganization(orgId),
    enabled: Boolean(orgId),
  })
  const orgName = orgDetailQ.data?.name || ''

  const centresQ = useQuery({
    queryKey: ['org-centres', orgId],
    queryFn: () => listOrgCentres(orgId),
    enabled: Boolean(orgId),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['org-centres', orgId] })

  const createMut = useMutation({
    mutationFn: () => createOrgCentre({
      organization: orgId,
      name: draft.name.trim(),
      location: draft.location.trim(),
      address: draft.address.trim(),
    }),
    onSuccess: (created) => {
      setShowAdd(false)
      invalidate()
      // Confirm what was created and under which org — the org name + code
      // prefix make a wrong-tenant filing obvious immediately.
      if (created) {
        setJustCreated({ name: created.name, code: created.code, orgName })
        setTimeout(() => setJustCreated(null), 12000)
      }
      setDraft({ name: '', location: '', address: '' })
    },
  })
  const regenMut = useMutation({
    mutationFn: (id) => regenerateOrgCentreCode(id),
    onSuccess: invalidate,
  })
  const deactivateMut = useMutation({
    mutationFn: (id) => deactivateOrgCentre(id),
    onSuccess: invalidate,
  })

  const rows = centresQ.data || []

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 1500)
    } catch {}
  }

  return (
    <Card>
      <CardHeader
        title="Centres &amp; centre-specific enrolment codes"
        subtitle={
          rows.length === 0
            ? 'Add a centre to give each site its own enrolment code. Staff using a centre code are auto-tagged with that site so workforce-health analytics can compare across locations. The organisation-wide code on the Members tab remains as a catch-all for staff with no specific site.'
            : `${rows.length} centre${rows.length === 1 ? '' : 's'} on file. Each has its own enrolment code that auto-tags staff with that site for cross-location analytics.`
        }
        right={
          !showAdd && (
            <button
              onClick={() => setShowAdd(true)}
              className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2"
            >
              + Add centre
            </button>
          )
        }
      />

      {justCreated && (
        <div className="mb-3 p-2.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-start justify-between gap-3">
          <span>
            Created <b>{justCreated.name}</b> under <b>{justCreated.orgName || 'this organisation'}</b>
            {' '}· code <code className="font-mono">{justCreated.code}</code>
          </span>
          <button
            onClick={() => setJustCreated(null)}
            className="text-emerald-700 hover:text-emerald-900 shrink-0"
          >Dismiss</button>
        </div>
      )}

      {showAdd && (
        <div className="mb-4 p-3 bg-wantok-50/60 border border-wantok-100 rounded-lg space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-wantok-800">
            New centre
          </div>
          <div className="text-xs rounded-md bg-white border border-wantok-200 px-2.5 py-1.5 text-slate-700">
            Adding centre to{' '}
            <b className="text-wantok-800">{orgName || '…'}</b>. It is filed under
            this organisation (the one selected above) — not anything typed in the name.
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-xs text-slate-600 flex flex-col gap-1">
              <span>Centre name *</span>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Popondetta"
                className="border rounded px-2 py-1.5 text-sm"
                autoFocus
              />
              <span className="text-[11px] text-slate-400">
                Site name only. Don’t include the organisation name — it’s filed
                under {orgName || 'the selected organisation'}.
              </span>
            </label>
            <label className="text-xs text-slate-600 flex flex-col gap-1">
              <span>Location (province / region)</span>
              <input
                value={draft.location}
                onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))}
                placeholder="e.g. Northern Province"
                className="border rounded px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs text-slate-600 flex flex-col gap-1 sm:col-span-2">
              <span>Address</span>
              <input
                value={draft.address}
                onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
                placeholder="Street / town"
                className="border rounded px-2 py-1.5 text-sm"
              />
            </label>
          </div>
          {createMut.isError && (
            <ErrorBanner error={createMut.error} />
          )}
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => { setShowAdd(false); setDraft({ name: '', location: '', address: '' }) }}
              className="text-xs text-slate-500 hover:text-slate-800"
            >Cancel</button>
            <button
              onClick={() => {
                const ok = window.confirm(
                  `Create centre “${draft.name.trim()}” under ${orgName || 'the selected organisation'}?\n\n` +
                  `It will belong to ${orgName || 'this organisation'} — make sure that is correct. ` +
                  `The organisation is the one selected at the top of the dashboard, not anything typed into the name or address.`
                )
                if (ok) createMut.mutate()
              }}
              disabled={!draft.name.trim() || createMut.isPending}
              className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
            >
              {createMut.isPending ? 'Creating…' : 'Create centre'}
            </button>
          </div>
        </div>
      )}

      {centresQ.isLoading && (
        <div className="text-sm text-slate-400 py-3">Loading centres…</div>
      )}
      {centresQ.error && <ErrorBanner error={centresQ.error} onRetry={() => centresQ.refetch()} />}

      {!centresQ.isLoading && rows.length === 0 && !showAdd && (
        <div className="text-sm text-slate-500 italic py-3">
          No centres yet. Click <b>+ Add centre</b> to create one.
        </div>
      )}

      {rows.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {rows.map((c) => (
            <li key={c.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800 truncate">{c.name}</div>
                <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-2">
                  {c.location && <span>{c.location}</span>}
                  {c.address && <span>· {c.address}</span>}
                  <span className="text-wantok-700 font-semibold">
                    {c.enrolled_count} enrolled
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <code className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-0.5 font-mono tracking-wider">
                    {c.code}
                  </code>
                  <button
                    onClick={() => copyToClipboard(c.code, `code-${c.id}`)}
                    className="text-xs text-wantok-700 underline font-semibold"
                  >
                    {copiedId === `code-${c.id}` ? 'Copied ✓' : 'Copy code'}
                  </button>
                  <button
                    onClick={() => copyToClipboard(c.enrolment_url, `url-${c.id}`)}
                    className="text-xs text-wantok-700 underline font-semibold"
                  >
                    {copiedId === `url-${c.id}` ? 'Copied ✓' : 'Copy signup link'}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => {
                    if (confirm(`Regenerate the enrolment code for "${c.name}"? Old code stops working immediately.`)) {
                      regenMut.mutate(c.id)
                    }
                  }}
                  disabled={regenMut.isPending}
                  className="text-xs border border-slate-200 hover:border-wantok-400 hover:text-wantok-800 rounded px-3 py-1.5"
                >
                  Regenerate code
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Deactivate "${c.name}"? Existing staff links are preserved.`)) {
                      deactivateMut.mutate(c.id)
                    }
                  }}
                  disabled={deactivateMut.isPending}
                  className="text-xs border border-rose-200 text-rose-700 hover:bg-rose-50 rounded px-3 py-1.5"
                >
                  Deactivate
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}


// ---------------------------------------------------------------------------
// creation happens at the accept-invite step (so a typo in the email
// can't accidentally grant access).
// ---------------------------------------------------------------------------
function CoAdminsCard({ orgId }) {
  const { user } = useAuth()
  const isPrimary = Boolean(user?.organization_admin_profile?.is_primary)
  const isPlatformAdmin = Boolean(user?.is_staff || user?.is_superuser)
  const canInvite = isPrimary || isPlatformAdmin
  const qc = useQueryClient()

  const adminsQ = useQuery({
    queryKey: ['org-admin-links', orgId],
    queryFn: () => listOrgAdminLinks({ orgId }),
    enabled: Boolean(orgId),
  })
  const invitesQ = useQuery({
    queryKey: ['org-coadmin-invites', orgId],
    queryFn: () => listCoAdminInvitations({ orgId }),
    enabled: Boolean(orgId),
  })

  function refetchAll() {
    qc.invalidateQueries({ queryKey: ['org-admin-links', orgId] })
    qc.invalidateQueries({ queryKey: ['org-coadmin-invites', orgId] })
  }

  return (
    <Card>
      <CardHeader
        title="Co-administrators"
        subtitle={
          canInvite
            ? 'People who can see this dashboard and manage the roster. Invite peers to share the load.'
            : 'People who can see this dashboard and manage the roster.'
        }
      />

      {adminsQ.error && <ErrorBanner error={adminsQ.error} />}
      {invitesQ.error && <ErrorBanner error={invitesQ.error} />}

      {/* Existing admins */}
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
        Active administrators
      </div>
      {adminsQ.isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (adminsQ.data || []).length === 0 ? (
        <div className="text-sm text-slate-400 italic">No administrators on file.</div>
      ) : (
        <ul className="divide-y divide-slate-100 mb-3">
          {(adminsQ.data || []).map((a) => (
            <li key={a.id} className="py-2 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {a.user_full_name || a.username}
                  {a.is_primary && (
                    <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-wantok-50 text-wantok-700 border-wantok-100">
                      primary
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 flex flex-wrap gap-2">
                  {a.user_email && <span>{a.user_email}</span>}
                  {a.title && <span>· {a.title}</span>}
                </div>
              </div>
              <span className="text-[11px] text-slate-400 whitespace-nowrap">
                Joined {a.created_at ? formatDateTime(a.created_at) : '—'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Pending invitations */}
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 mt-2">
        Pending invitations
      </div>
      {invitesQ.isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (invitesQ.data || []).filter((i) => i.status === 'pending').length === 0 ? (
        <div className="text-sm text-slate-400 italic">No pending invitations.</div>
      ) : (
        <ul className="divide-y divide-slate-100 mb-3">
          {(invitesQ.data || [])
            .filter((i) => i.status === 'pending')
            .map((i) => (
              <PendingInviteRow key={i.id} invite={i} onChanged={refetchAll} />
            ))}
        </ul>
      )}

      {/* Invite form */}
      {canInvite && <InviteCoAdminForm orgId={orgId} onInvited={refetchAll} />}

      {!canInvite && (
        <div className="text-[11px] text-slate-500 italic mt-2">
          Only the primary administrator can invite co-admins. Ask
          {' '}{(adminsQ.data || []).find((a) => a.is_primary)?.user_email || 'your primary admin'}.
        </div>
      )}
    </Card>
  )
}

function PendingInviteRow({ invite, onChanged }) {
  const revoke = useMutation({
    mutationFn: () => revokeCoAdminInvitation(invite.id),
    onSuccess: onChanged,
  })
  const resend = useMutation({
    mutationFn: () => resendCoAdminInvitation(invite.id),
    onSuccess: onChanged,
  })
  return (
    <li className="py-2 flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-800 truncate">
          {(invite.first_name || '') + ' ' + (invite.last_name || '')}
          <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-amber-50 text-amber-700 border-amber-200">
            pending
          </span>
        </div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-2">
          {invite.email && <span>{invite.email}</span>}
          {invite.is_admin && <span>· primary</span>}
          {invite.created_at && <span>· sent {formatDateTime(invite.created_at)}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => resend.mutate()}
          disabled={resend.isPending}
          className="text-xs text-wantok-700 underline disabled:opacity-40"
        >
          {resend.isPending ? 'Sending…' : 'Resend'}
        </button>
        <button
          onClick={() => {
            if (window.confirm(`Revoke the invitation for ${invite.email}?`)) {
              revoke.mutate()
            }
          }}
          disabled={revoke.isPending}
          className="text-xs text-rose-600 hover:underline disabled:opacity-40"
        >
          Revoke
        </button>
      </div>
    </li>
  )
}

function InviteCoAdminForm({ orgId, onInvited }) {
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [makePrimary, setMakePrimary] = useState(false)
  const [note, setNote] = useState('')

  const invite = useMutation({
    mutationFn: () => inviteCoAdmin({
      orgId,
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      isPrimary: makePrimary,
      note: note.trim(),
    }),
    onSuccess: () => {
      setEmail(''); setFirstName(''); setLastName(''); setMakePrimary(false); setNote('')
      onInvited()
    },
  })

  const canSubmit = email.trim() && firstName.trim() && lastName.trim() && !invite.isPending

  return (
    <div className="border-t pt-3 mt-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
        Invite a co-administrator
      </div>
      <div className="grid gap-2 md:grid-cols-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@company.com"
          className="border rounded px-2 py-1.5 text-sm"
        />
        <input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name"
          className="border rounded px-2 py-1.5 text-sm"
        />
        <input
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last name"
          className="border rounded px-2 py-1.5 text-sm"
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <label className="text-xs text-slate-600 flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={makePrimary}
            onChange={(e) => setMakePrimary(e.target.checked)}
          />
          Make primary administrator on accept
        </label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (e.g. 'wellness lead, North office')"
          className="flex-1 min-w-[12rem] border rounded px-2 py-1.5 text-sm"
        />
        <button
          onClick={() => invite.mutate()}
          disabled={!canSubmit}
          className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-1.5 disabled:opacity-40"
        >
          {invite.isPending ? 'Sending…' : 'Send invite'}
        </button>
      </div>
      {invite.error && <div className="mt-2"><ErrorBanner error={invite.error} /></div>}
      {invite.isSuccess && (
        <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
          Invitation sent. The recipient will get an email with a link to accept.
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Bulk import card — two-step CSV roster import. "Analyse roster" runs a
// server-side dry run and shows what WOULD happen per row (new person /
// linked to org / link updated / error) without writing anything; "Apply
// import" then commits. Identity is matched on employee_id (within the
// org) first, then email, so the same person is never imported twice.
// New users get a password-reset email with a 72h token (the existing
// reset template reused with a custom subject).
// ---------------------------------------------------------------------------
// Sample CSV — kept inline as a data: URL so it works offline and
// renames cleanly via the download attribute. The `centre` column is
// optional but worth including in the template because the moment a
// multi-site org (e.g. OPIC with five centres) starts using bulk import
// without it, per-centre workforce-health analytics fall to zero.
// Accepts either the centre's human name ("Port Moresby") or its short
// enrolment code ("OILPORRXUK"); the server resolves either form against
// the org's active centres.
const ROSTER_SAMPLE_CSV =
  'data:text/csv;charset=utf-8,' +
  'first_name,last_name,email,phone,date_of_birth,sex,department,role,employee_id,centre,notes%0A' +
  'Joseph,Iamo,joseph.iamo@example.com,%2B675%207000%201234,1985-03-12,male,Operations,Foreman,EMP-001,Port%20Moresby,%0A' +
  'Maria,Kila,,%2B675%207000%205678,1990-07-25,female,Logistics,Coordinator,EMP-002,Alotau,No%20email%20on%20file'

function BulkImportCard({ orgId }) {
  const qc = useQueryClient()
  const fileRef = useRef(null)
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)   // dry-run result
  const [applied, setApplied] = useState(null)   // committed result

  const analyze = useMutation({
    mutationFn: () => bulkUploadOrgRoster({ file, orgId, dryRun: true }),
    onSuccess: (data) => { setPreview(data); setApplied(null) },
  })

  const apply = useMutation({
    mutationFn: () => bulkUploadOrgRoster({ file, orgId, dryRun: false }),
    onSuccess: (data) => {
      setApplied(data)
      setPreview(null)
      qc.invalidateQueries({ queryKey: ['org-members', orgId] })
      qc.invalidateQueries({ queryKey: ['org-detail', orgId] })
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
    },
  })

  function pickFile(f) {
    setFile(f)
    setPreview(null)
    setApplied(null)
    analyze.reset()
    apply.reset()
  }

  const willImport = preview
    ? (preview.counts?.create || 0) + (preview.counts?.link || 0) + (preview.counts?.update || 0)
    : 0

  return (
    <Card>
      <CardHeader
        title="Bulk roster import"
        subtitle="CSV with first_name, last_name and an email or employee_id for each person. Optional columns: phone, date_of_birth (YYYY-MM-DD or DD/MM/YYYY), sex (male/female/other), department, role, centre, notes. Date of birth drives the age-based screening / fitness-for-work assessment, so include it for occupational-health rosters. The centre column accepts the centre's name or its enrolment code — staff are auto-tagged so per-site workforce-health analytics work from day one. Analyse first to preview the matches, then apply — people are matched on employee ID and email so no one is imported twice."
      />
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => pickFile(e.target.files?.[0] || null)}
          className="text-sm"
        />
        <button
          onClick={() => file && analyze.mutate()}
          disabled={!file || analyze.isPending}
          className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-1.5 disabled:opacity-40"
        >
          {analyze.isPending ? 'Analysing…' : 'Analyse roster'}
        </button>
        {preview && (
          <button
            onClick={() => apply.mutate()}
            disabled={apply.isPending || willImport === 0}
            className="text-sm bg-emerald-700 hover:bg-emerald-800 text-white rounded px-3 py-1.5 disabled:opacity-40"
          >
            {apply.isPending ? 'Applying…' : `Apply import (${willImport})`}
          </button>
        )}
        <a
          href={ROSTER_SAMPLE_CSV}
          download="wantok_roster_template.csv"
          className="text-xs text-wantok-700 underline"
        >
          Download sample CSV
        </a>
        {file && <span className="text-xs text-slate-500">{file.name} · {Math.round(file.size / 1024)} KB</span>}
      </div>
      {(analyze.error || apply.error) && (
        <div className="mt-3"><ErrorBanner error={analyze.error || apply.error} /></div>
      )}
      {preview && <RosterPreview result={preview} />}
      {applied && <RosterPreview result={applied} />}
    </Card>
  )
}

// Renders the per-row breakdown for both the dry-run preview and the
// committed result — the server returns the same shape for each.
function RosterPreview({ result }) {
  const counts = result?.counts || {}
  const rows = result?.rows || []
  const dry = result?.dry_run
  const attention = rows.filter((r) => r.action === 'error' || r.note)
  const errorCount = counts.error || 0

  return (
    <div className="mt-4 space-y-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">
        {dry ? 'Preview — nothing has been saved yet' : 'Import applied'}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <ImportStat label={dry ? 'New people' : 'People created'} value={counts.create} colour="amber" />
        <ImportStat label={dry ? 'To link to org' : 'Linked to org'} value={counts.link} colour="emerald" />
        <ImportStat label={dry ? 'To update' : 'Links updated'} value={counts.update} colour="sky" />
        <ImportStat label="Errors (skipped)" value={errorCount} colour={errorCount ? 'rose' : 'slate'} />
      </div>
      {attention.length > 0 && (
        <div className="border border-slate-200 rounded overflow-hidden">
          <div className="text-[11px] uppercase tracking-wide text-slate-600 bg-slate-50 px-2 py-1">
            Rows needing attention ({attention.length})
          </div>
          <div className="max-h-56 overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="px-2 py-1 font-medium">Row</th>
                  <th className="px-2 py-1 font-medium">Name</th>
                  <th className="px-2 py-1 font-medium">Outcome</th>
                  <th className="px-2 py-1 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {attention.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="px-2 py-1 font-mono text-slate-500">{r.row}</td>
                    <td className="px-2 py-1">{r.name || '—'}</td>
                    <td className="px-2 py-1">
                      <RosterActionBadge action={r.action} matchedBy={r.matched_by} />
                    </td>
                    <td className={`px-2 py-1 ${r.error ? 'text-rose-700' : 'text-amber-700'}`}>
                      {r.error || r.note}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {attention.length === 0 && (
        <div className="text-xs text-emerald-700">
          All {result.rows_processed} row{result.rows_processed === 1 ? '' : 's'} are clean.
        </div>
      )}
      {dry && errorCount > 0 && (
        <div className="text-[11px] text-slate-500">
          Error rows are skipped on apply — the rest still import.
        </div>
      )}
    </div>
  )
}

function RosterActionBadge({ action, matchedBy }) {
  const map = {
    create: ['New person', 'bg-amber-100 text-amber-800'],
    link: ['Link to org', 'bg-emerald-100 text-emerald-800'],
    update: ['Update', 'bg-sky-100 text-sky-800'],
    error: ['Error', 'bg-rose-100 text-rose-800'],
  }
  const [label, cls] = map[action] || ['—', 'bg-slate-100 text-slate-600']
  const by = matchedBy === 'employee_id' ? ' · matched on employee ID'
    : matchedBy === 'email' ? ' · matched on email' : ''
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>
      {label}{by}
    </span>
  )
}

function ImportStat({ label, value, colour = 'slate' }) {
  const map = {
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    sky: 'border-sky-200 bg-sky-50 text-sky-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
  }
  return (
    <div className={`border rounded px-2 py-1.5 text-center ${map[colour]}`}>
      <div className="text-lg font-semibold">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
    </div>
  )
}
